/**
 * Utilities for building the shell bundle API payload (POST /v1/config-tools/sync/create-shell-bundle).
 * New format: single object with projectId, sourceEnvId, targetEnvironments, items.
 * Each item has targets array with one entry per target env: { envId, targetValue, oldValue, status }.
 */

type TargetEnvironmentTe = {
  id: number;
  environmentName?: string;
  environment_name?: string;
  type?: string;
  stackName?: string;
  stack_name?: string;
};

export type ShellBundleTargetEnv = { id: number; name: string };

export type ShellBundlePayload = {
  projectId: string;
  sourceEnvId: number;
  targetEnvironments: ShellBundleTargetEnv[];
  items: Array<{
    category: string;
    key: string;
    sourceValue: any;
    targets: Array<{ envId: number; targetValue: string; oldValue?: string; status?: string }>;
    metadata?: Record<string, any>;
  }>;
};

function safeStringify(v: any): string {
  if (v === null || typeof v === 'undefined') return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * Derive short name for BE zip filenames (e.g. nonprod-configs-77777.sh).
 * Uses first segment from environmentName before underscore, lowercased, or type as fallback.
 */
export function deriveEnvName(te: TargetEnvironmentTe): string {
  const envName = String(te.environmentName ?? te.environment_name ?? '').trim();
  if (envName) {
    const first = envName.split('_')[0];
    if (first) return first.toLowerCase();
  }
  const type = String(te.type ?? '').trim().toLowerCase();
  if (type) return type;
  return `env${te.id}`;
}

/**
 * Build shell bundle payload in new API format.
 * Converts FE items (per-env, envValues, targets, or single-target) to unified items with targets array.
 */
export function buildShellBundlePayload(
  items: any[],
  targetEnvironments: TargetEnvironmentTe[],
  sourceEnvId: number,
  projectId: string
): ShellBundlePayload {
  const targetEnvsForApi: ShellBundleTargetEnv[] = targetEnvironments.map((te) => ({
    id: Number(te.id),
    name: deriveEnvName(te),
  }));

  const itemsByKey = new Map<
    string,
    {
      category: string;
      key: string;
      sourceValue: any;
      metadata?: Record<string, any>;
      envValues: Map<number, { targetValue: string; oldValue: string; status: string }>;
    }
  >();

  const getItemKey = (item: any) => {
    const category = String(
      item.category ?? 
      item.metadata?.category ?? 
      'configs'
    );
    const key = String(
      item.metadata?.configKey ?? 
      item.key ?? 
      item.configKey ??
      item.metadata?.key ??
      (item.itemId != null ? String(item.itemId) : '')
    );
    return `${category}::${key}`;
  };

  const upsertEnvValue = (
    mapKey: string,
    category: string,
    key: string,
    sourceValue: any,
    metadata: Record<string, any> | undefined,
    envId: number,
    targetValue: string,
    status: string,
    oldValue?: string
  ) => {
    if (!itemsByKey.has(mapKey)) {
      itemsByKey.set(mapKey, {
        category,
        key,
        sourceValue,
        metadata,
        envValues: new Map(),
      });
    }
    const data = itemsByKey.get(mapKey)!;
    data.envValues.set(envId, { targetValue, oldValue: oldValue ?? '', status });
  };

  for (const item of items) {
    // Extract category - try multiple possible fields
    const category = String(
      item.category ?? 
      item.metadata?.category ?? 
      (item.itemId != null ? 'configs' : 'configs')
    );
    
    // Extract key - try multiple possible fields and locations
    const key = String(
      item.metadata?.configKey ?? 
      item.key ?? 
      item.configKey ??
      item.metadata?.key ??
      (item.itemId != null ? String(item.itemId) : '')
    );
    
    // Skip items with empty key as they cannot be processed
    if (!key || key.trim() === '') {
      console.warn('Skipping item with empty key:', item);
      continue;
    }
    
    const mapKey = getItemKey(item);
    const sourceValue = item.sourceValue ?? item.source_value ?? '';
    const metadata = item.metadata && typeof item.metadata === 'object' ? item.metadata : undefined;

    if ((item as any).envId != null) {
      const envId = Number((item as any).envId);
      const targetValue = safeStringify(item.targetValue ?? item.target_value ?? '');
      const oldValue = safeStringify((item as any).oldValue ?? (item as any).old_value ?? '');
      const status = String(item.status ?? 'unchanged').toLowerCase();
      upsertEnvValue(mapKey, category, key, sourceValue, metadata, envId, targetValue, status, oldValue);
    } else if (
      item.metadata?.isEnvVar === true &&
      (item as any).envValues &&
      typeof (item as any).envValues === 'object'
    ) {
      const envValues = (item as any).envValues;
      if (envValues instanceof Map) {
        envValues.forEach((val: any, envId: number) => {
          const targetValue = safeStringify(val);
          upsertEnvValue(mapKey, category, key, sourceValue, metadata, Number(envId), targetValue, 'unchanged');
        });
      } else {
        const obj = envValues as Record<string | number, any>;
        Object.entries(obj).forEach(([k, v]) => {
          const envId = Number(k);
          if (!Number.isNaN(envId)) {
            const targetValue = safeStringify(v);
            upsertEnvValue(mapKey, category, key, sourceValue, metadata, envId, targetValue, 'unchanged');
          }
        });
      }
    } else if (Array.isArray(item.targets) && item.targets.length > 0) {
      for (const t of item.targets) {
        // Support both nested (targetEnv.id) and flat (envId/env_id) structures
        const tg = t.targetEnv;
        const rawEnvId = tg?.id ?? tg?.envId ?? t?.envId ?? t?.env_id ?? t?.id;
        const envId = typeof rawEnvId === 'number' ? rawEnvId : Number(rawEnvId);
        if (!Number.isNaN(envId)) {
          const targetValue = safeStringify(t.targetValue ?? t.target_value ?? '');
          const oldValue = safeStringify((t as any).oldValue ?? (t as any).old_value ?? '');
          const status = String(t.status ?? 'unchanged').toLowerCase();
          upsertEnvValue(mapKey, category, key, sourceValue, metadata, envId, targetValue, status, oldValue);
        }
      }
    } else {
      const targetValue = safeStringify(item.targetValue ?? item.target_value ?? '');
      const status = String(item.status ?? 'unchanged').toLowerCase();
      for (const te of targetEnvironments) {
        const envId = Number(te.id);
        upsertEnvValue(mapKey, category, key, sourceValue, metadata, envId, targetValue, status);
      }
    }
  }

  const unifiedItems = Array.from(itemsByKey.entries()).map(([, data]) => {
    const targets = targetEnvsForApi.map((te) => {
      const entry = data.envValues.get(te.id);
      return {
        envId: te.id,
        targetValue: entry?.targetValue ?? '',
        oldValue: entry?.oldValue ?? '',
        status: entry?.status ?? 'unchanged',
      };
    });
    return {
      category: data.category,
      key: data.key,
      sourceValue: safeStringify(data.sourceValue),
      targets,
      ...(data.metadata ? { metadata: data.metadata } : {}),
    };
  });

  return {
    projectId: String(projectId),
    sourceEnvId: Number(sourceEnvId),
    targetEnvironments: targetEnvsForApi,
    items: unifiedItems,
  };
}
